/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  Autocomplete,
  Button,
  FormControl,
  Grid,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import PageLayout from "../components/layout/PageLayout";

import { DateTimePicker, renderTimeViewClock } from "@mui/x-date-pickers";

import {
  MOVEMENTS_NEXT_ADJUSTMENT_DOC_URL,
  MOVEMENTS_URL,
} from "constants/urls";
import useCrud from "hooks/useCrud";
import {
  ArticleLocation,
  AssociatedClient,
  Movement,
  MovementAdditionalItemDraft,
  MovementArticle,
} from "types";

import FileImport from "components/FileImport/FileImport";
import MovementAdditionalItemsStepModal, {
  MovementAdditionalItemsConfirmPayload,
} from "components/Operations/MovementAdditionalItemsStepModal";
import OperationPrintDetails from "components/Prints/Operations/OperationPrintDetails";
import OperationPrintHeaderDetails from "components/Prints/Operations/OperationPrintHeaderDetails";
import { PrintLayout } from "components/Prints/PrintLayout";
import Loader from "components/lists/Loader";
import DateTimePickerActionBar from "components/utils/DatePickerActionBar";
import { dateISOFormat, dateToMySQLFormat } from "helpers";
import { useConfirmationModal } from "hooks/useConfirmationModal";
import { useToast } from "hooks/useToast";
import { useSession } from "providers/SessionProvider";
import { useNavigate, useParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import ArticlesList from "../components/Operations/ArticlesList";
import {
  DATE_VIEWS,
  MOVEMENT_TYPES,
  MovementTypes,
  Roles,
  STATUS_CODES,
  defaultMovement,
} from "../constants";
import { decimalFormat } from "helpers/formatNumber";
import CustomNumberInput from "components/CustomNumberInput";
import { ENTITY } from "constants/entities";
import {
  calculateMovementTotals,
  getInitialMovementDate,
  initMovementDate,
} from "helpers/movement";
import { OperationPrintTotals } from "components/Prints/Operations/OperationPrintTotal";
import { hydrateDraftMovementArticles } from "helpers/movementDraftLineData";
import {
  buildRequiredAdditionalItemDrafts,
  buildAdditionalMovementsPayload,
  getAdditionalItemsModalTitle,
  getMissingRequiredAdditionalItems,
} from "helpers/additionalMovements";

export type GroupedMovementArticles = {
  article_location_id: number;
  lote: number | null;
  article_locations: ArticleLocation;
  amount_per_container: number;
  oc?: string;
  fecha_vencimiento?: string;
};

export type DischargesPageProps = {
  isForLiquidation?: boolean;
  clientId?: number;
  storeId?: number;
  minDate?: Date;
  maxDate?: Date;
  onMovementChange?: (movement: Movement) => void;
  extraMovement?: Movement;
};

export default function DischargesPage({
  isForLiquidation = false,
  clientId,
  storeId,
  minDate,
  maxDate = new Date(),
  onMovementChange,
  extraMovement,
}: DischargesPageProps) {
  const { movement_draft_id } = useParams();
  //context
  const {
    stores,
    additionalItems,
    clients,
    userData,
    associatedClients,
    locations,
    articles,
    users,
    updateSessionData,
  } = useSession();

  //custom hooks
  const { createData: createMovement } = useCrud<Movement[]>(MOVEMENTS_URL, {
    disableAutoFetch: true,
  });
  const { createData: convertOrderToDischarge } = useCrud<Movement[]>(
    `${MOVEMENTS_URL}order-to-discharges`,
    { disableAutoFetch: true },
  );
  const {
    data: movements,
    firstLoad: onMovementFirstLoad,
    fetchData: getMovements,
  } = useCrud<Movement>(MOVEMENTS_URL, { disableAutoFetch: true });

  const { showModal } = useConfirmationModal();
  const { showSnackBar } = useToast();
  const navigate = useNavigate();

  //local states
  const [movementLoading, setMovementLoading] = useState<boolean>(false);
  const [additionalItemsModalOpen, setAdditionalItemsModalOpen] = useState(false);
  const [additionalItemsInitialItems, setAdditionalItemsInitialItems] =
    useState<MovementAdditionalItemDraft[]>([]);
  const [newPrepOrder, setNewPrepOrder] = useState<boolean>(false);
  const [isPalletCalculationEnabled, setIsPalletCalculationEnabled] =
    useState<boolean>(true);
  const [isWeightCalculationEnabled, setIsWeightCalculationEnabled] =
    useState<boolean>(true);
  const [movement, setMovement] = useState<Movement>(
    extraMovement || {
      ...defaultMovement,
      movement_type_id: MovementTypes.Discharge,
      movement_draft_id: movement_draft_id
        ? Number(movement_draft_id)
        : undefined,
      isAdjustment: false,
      movement_id: null,
    },
  );
  const [articleRowIndex, setArticleRowIndex] = useState<number>(0);
  const [mustCalculatePallets, setMustCalculatePallets] =
    useState<boolean>(!extraMovement);
  const [movementToPrint, setMovementToPrint] = useState<Movement | null>(null);
  const [additionalMovementsToPrint, setAdditionalMovementsToPrint] = useState<Movement[]>([]);

  const [movementToInitialize, setMovementToInitialize] = useState<
    Movement | undefined
  >(extraMovement);

  const [selectedDelivery, setSelectedDelivery] = useState<{
    id: number | undefined;
    label: string;
  }>({ id: 0, label: "Egreso Directo" });
  const [selectedUser, setSelectedUser] = useState<{
    id: number;
    label: string;
  } | null>(
    userData
      ? {
          id: userData.id,
          label: `${userData.first_name} ${userData.last_name}`,
        }
      : null,
  );
  const [selectedAuthor, setSelectedAuthor] = useState<{
    id: number;
    label: string;
  } | null>(null);
  const [selectedClient, setSelectedClient] = useState<{
    id: number;
    label: string;
  } | null>(null);
  const [selectedAssociatedClient, setSelectedAssociatedClient] = useState<{
    id: number;
    label: string;
  } | null>(null);
  const selectedFullClient = useMemo(
    () => clients.find((c) => c.id === movement.client_id) ?? null,
    [clients, movement.client_id],
  );

  const scopedClientIds = useMemo(
    () => [
      movement.client_id,
      ...clients
        .filter((client) => client.parent_client_id === movement.client_id)
        .map((client) => client.id),
    ],
    [clients, movement.client_id],
  );

  const childClients = useMemo(
    () =>
      clients.filter(
        (client) => client.parent_client_id === movement.client_id,
      ),
    [clients, movement.client_id],
  );

  const clientAdditionalItems = useMemo(
    () =>
      additionalItems.filter(
        (item) =>
          item.client_id == null || scopedClientIds.includes(item.client_id),
      ),
    [additionalItems, scopedClientIds],
  );

  const { fetchData: fetchMovements } = useCrud(MOVEMENTS_URL);
  const [availableMovements, setAvailableMovements] = useState<Movement[]>([]);

  const { fetchData: fetchNextAdjustmentDoc } = useCrud<{
    client_document: string;
  }>(MOVEMENTS_NEXT_ADJUSTMENT_DOC_URL, { disableAutoFetch: true });

  const setMovementDate = (field: string, value: string) => {
    handleChange(field as keyof Movement, value);
  };

  //memozation
  const movementTotals = useMemo(
    () => calculateMovementTotals(movement),
    [movement],
  );

  const nroOrden = useMemo(() => {
    if (movements.length > 0) {
      const lastMovement = movements[movements.length - 1];
      if (lastMovement.id) {
        return lastMovement.id + 1;
      }
    }
    return 1;
  }, [movements]);

  const prepList = useMemo(() => {
    const movList = movements
      .filter((m) => m.movement_type_id === MovementTypes.Preparation)
      .map((m) => ({ id: m.id, label: `Pedido ${m.id}` }));
    movList.unshift({ id: 0, label: "Egreso Directo" });
    return movList;
  }, [movements]);

  const storesList = useMemo(() => {
    const storesL = stores.map((s) => ({ id: s.id, label: s.description }));
    storesL.unshift({ id: 0, label: "Todos" });
    return storesL;
  }, [stores]);
  const selectedWarehouse = useMemo(() => {
    if (movement.store_id === null) {
      return { id: 0, label: "Todos" };
    }

    const store = storesList.find((s) => s.id === movement.store_id);

    return store ? { id: store.id, label: store.label } : null;
  }, [storesList, movement.store_id]);

  const clientAssociatedClients = useMemo(() => {
    return associatedClients.filter(
      (ac: AssociatedClient) => ac.client?.id === movement.client_id,
    );
  }, [movement.client_id]);

  //events
  const printRef = useRef(null);
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    onAfterPrint: () => {
      if (additionalMovementsToPrint.length > 0) {
        const [nextMovementToPrint, ...remainingMovementsToPrint] =
          additionalMovementsToPrint;

        setAdditionalMovementsToPrint(remainingMovementsToPrint);
        setMovementToPrint(nextMovementToPrint);
        return;
      }

      if (newPrepOrder) {
        setNewPrepOrder(false);
        setMovementLoading(false);
        showSnackBar(
          "Se ha creado el pedido a preparar correctamente",
          "success",
        );
        setMovementToPrint(null);
      } else {
        navigate("/operations");
        setMovementLoading(false);
        showSnackBar("Se ha creado el egreso correctamente", "success");
      }
    },
  });

  const handleChange = useCallback(
    <K extends keyof Movement>(
      name: K,
      value: Movement[K] | null | undefined | string,
    ) => {
      setMovement((prev: Movement) => ({
        ...prev,
        [name]: value,
      }));
    },
    [],
  );

  const handleSendDraft = () => {
    handleSubmit(false);
  };

  const handleSubmit = async (
    print: boolean = true,
    additionalItemsToCreate: MovementAdditionalItemDraft[] = [],
    additionalMovementHeader?: Movement,
  ) => {
    setMovementLoading(true);

    if (selectedDelivery.id === 0 || newPrepOrder) {
      const movementResponse = await createMovement(
        [
          {
            ...movement,
            additional_items: print ? [] : movement.additional_items,
            movement_type_id: print
              ? movement.movement_type_id
              : movement.movement_type_id === MovementTypes.Preparation
                ? MovementTypes.PreparationDraft
                : MovementTypes.DischargeDraft,
            movement_draft_id: movement_draft_id
              ? Number(movement_draft_id)
              : undefined,
          },
        ],
        true,
      );
      if (movementResponse.status === STATUS_CODES.OK) {
        const aggregatedMovementResponse = await getMovements(
          `${movementResponse.data[0].id}`,
          true,
        );

        const createdMovement = aggregatedMovementResponse.data[0];

        if (print && additionalItemsToCreate.length > 0) {
          const additionalMovementsPayload = buildAdditionalMovementsPayload({
            baseMovement: movement,
            headerMovement: additionalMovementHeader,
            items: additionalItemsToCreate,
            originMovementId: movement.isAdjustment && movement.movement_id
              ? movement.movement_id
              : createdMovement.id,
          });

          const additionalMovementResponse = await createMovement(
            additionalMovementsPayload,
            true,
          );

          if (additionalMovementResponse.status !== STATUS_CODES.OK) {
            setMovementLoading(false);
            showSnackBar(
              additionalMovementResponse.data?.message ||
                "El egreso se creó, pero no se pudieron crear los adicionales asociados.",
              "error",
            );
            return;
          }
          
          if (Array.isArray(additionalMovementResponse.data)) {
            setAdditionalMovementsToPrint(additionalMovementResponse.data);
          }
        }

        setMovementToPrint(createdMovement);

        if (createdMovement?.articles?.length > 0) {
          createdMovement.articles.forEach((ma: MovementArticle) => {
            const baseArticle = ma.article_locations.article;
            const sessionArticle = articles.find(
              (a) => a.id === baseArticle.id,
            );

            updateSessionData(ENTITY.ARTICLES, {
              ...baseArticle,
              amount: Math.max(
                0,
                (sessionArticle?.amount ?? 0) - (ma.amount ?? 0),
              ),
            });
          });
        }

        if (print) {
          setMovementToPrint(createdMovement);
        } else {
          navigate("/operations");
          setMovementLoading(false);
          showSnackBar(
            "Se ha creado el borrrador del " +
              (movement.movement_type_id === MovementTypes.Preparation
                ? "pedido"
                : "egreso") +
              " correctamente.",
            "success",
          );
        }
      } else {
        setMovementLoading(false);
        showSnackBar(
          movementResponse.data?.message ||
            "El " +
              (movement.movement_type_id === MovementTypes.Preparation
                ? "pedido"
                : "egreso") +
              " no ha podido crearse, por favor intente nuevamente",
          "error",
        );
      }
    } else {
      const resp = await convertOrderToDischarge([movement], true);

      if (resp.status === STATUS_CODES.OK) {
        const createdId = resp.data?.[0]?.id ?? resp.data?.id;
        const aggregatedMovementResponse = await getMovements(
          `${createdId}`,
          true,
        );
        setMovementToPrint(aggregatedMovementResponse.data[0]);
      } else {
        showSnackBar(
          "El egreso no ha podido crearse, por favor intente nuevamente",
          "error",
        );
      }
    }

    setMovementLoading(false);
  };

  const handleConfirmAdditionalItems = ({
    movement: additionalMovementHeader,
    items,
  }: MovementAdditionalItemsConfirmPayload) => {
    const shouldValidateRequiredAdditionalItems =
      selectedDelivery.id === 0 && !newPrepOrder;

    if (shouldValidateRequiredAdditionalItems) {
      const missingRequiredItems = getMissingRequiredAdditionalItems({
        movement,
        clients,
        additionalItems,
        selectedItems: items,
        movementType: "discharge",
      });

      if (missingRequiredItems.length > 0) {
        showModal({
          title: "Adicional obligatorio",
          message:
            missingRequiredItems.length === 1
              ? `El adicional "${missingRequiredItems[0].additionalItem.description}" es obligatorio para este egreso.`
              : `Hay ${missingRequiredItems.length} adicionales obligatorios pendientes para este egreso: ${missingRequiredItems
                  .map((item) => item.additionalItem.description)
                  .join(", ")}.`,
          confirmLabel:
            missingRequiredItems.length === 1 ? "Aplicar" : "Aplicar todos",
          action: async () => {
            const requiredItems =
              buildRequiredAdditionalItemDrafts(missingRequiredItems);

            setAdditionalItemsInitialItems([...items, ...requiredItems]);
          },
        });

        return;
      }
    }

    setAdditionalItemsModalOpen(false);
    setAdditionalItemsInitialItems([]);
    handleSubmit(true, items, additionalMovementHeader);
  };

  const calculateTotalPallets = useCallback(() => {
    const totalPallets = movement.articles.reduce((sum, article) => {
      const unitsPerPallet = article.article_locations.article.units_per_pallet;
      const amount = article.amount;
      const palletsForArticle = unitsPerPallet
        ? Math.ceil(amount / unitsPerPallet)
        : 0;
      return sum + palletsForArticle;
    }, 0);
    setMovement((prev) => ({
      ...prev,
      pallets: decimalFormat(totalPallets),
    }));
  }, [movement.articles]);

  //side effects
  useEffect(() => {
    const setMovementData = async () => {
      await getMovements(
        undefined,
        true,
        { movementTypeId: MovementTypes.Preparation },
        true,
      );
      setMovement((prev) => ({
        ...defaultMovement,
        ...prev,
        created_by: selectedUser?.id ?? prev.created_by,
        movement_type_id: newPrepOrder
          ? MovementTypes.Preparation
          : MovementTypes.Discharge,
        date: prev.date ?? defaultMovement.date,
        store_id: defaultMovement.store_id,
      }));
      setSelectedAuthor(null);
      setSelectedClient(null);
      setSelectedAssociatedClient(null);
    };
    if (newPrepOrder && !movement_draft_id) {
      setMovementData();
    }
  }, [newPrepOrder]);

  useEffect(() => {
    let cancelled = false;

    const getSelectedDelivery = async () => {
      const selectedMovement = movements.find(
        (m) => m.id === selectedDelivery.id,
      );

      if (selectedMovement && Array.isArray(selectedMovement.articles)) {
        return selectedMovement;
      }

      const response = await fetchMovements(`${selectedDelivery.id}`, true);
      const movementDetail = Array.isArray(response.data)
        ? response.data[0]
        : response.data;

      return (movementDetail ?? selectedMovement) as Movement | undefined;
    };

    if (selectedDelivery.id === 0) {
      setIsPalletCalculationEnabled(true);
      setIsWeightCalculationEnabled(true);
      setMustCalculatePallets(!movementToInitialize);

      const initializedStoreId = movementToInitialize
        ? movementToInitialize.store_id
        : storeId === undefined
          ? defaultMovement.store_id
          : storeId;

      setMovement((prev) => ({
        ...defaultMovement,
        client_id:
          movementToInitialize?.client_id ||
          clientId ||
          defaultMovement.client_id,
        created_by: userData ? userData.id : defaultMovement.created_by,
        movement_type_id:
          movementToInitialize?.movement_type_id ===
          MovementTypes.PreparationDraft
            ? MovementTypes.Preparation
            : MovementTypes.Discharge,
        store_id: initializedStoreId,
        updated_by: movementToInitialize
          ? movementToInitialize.updated_by
          : defaultMovement.updated_by,
        date: prev.date || movementToInitialize?.date || defaultMovement.date,
        associated_client:
          movementToInitialize?.associated_client ||
          defaultMovement.associated_client,
        client_document:
          movementToInitialize?.client_document ||
          defaultMovement.client_document,
        pallets: movementToInitialize?.pallets || defaultMovement.pallets,
        weight: movementToInitialize?.weight || defaultMovement.weight,
        numero_remito:
          movementToInitialize?.numero_remito || defaultMovement.numero_remito,
        observation:
          movementToInitialize?.observation || defaultMovement.observation,
        articles: movementToInitialize?.articles || [],
        additional_items: movementToInitialize?.additional_items || [],
      }));
      setSelectedUser(
        userData
          ? {
              id: userData.id,
              label: `${userData.first_name} ${userData.last_name}`,
            }
          : null,
      );
      setSelectedAuthor(
        movementToInitialize?.updated_by
          ? {
              id: movementToInitialize.updated_by,
              label: `${
                users.find((u) => u.id === movementToInitialize.updated_by)
                  ?.first_name
              } ${
                users.find((u) => u.id === movementToInitialize.updated_by)
                  ?.last_name
              }`,
            }
          : null,
      );
      const selectedClientId = movementToInitialize?.client_id || clientId;
      setSelectedClient(
        selectedClientId
          ? {
              id: selectedClientId,
              label:
                clients.find((c) => c.id === selectedClientId)?.razon_social ||
                "",
            }
          : null,
      );
      setSelectedAssociatedClient(
        movementToInitialize?.associated_client
          ? {
              id:
                clientAssociatedClients.find(
                  (ac) =>
                    ac.razon_social === movementToInitialize.associated_client,
                )?.id ?? 0,
              label: movementToInitialize.associated_client,
            }
          : null,
      );
      if (movementToInitialize) {
        setArticleRowIndex(movementToInitialize.articles?.length ?? 0);
      }
    } else {
      const setSelectedDeliveryMovement = async () => {
        setMovementLoading(true);
        setIsPalletCalculationEnabled(false);
        setIsWeightCalculationEnabled(false);
        setMustCalculatePallets(false);

        try {
          const delivery = await getSelectedDelivery();

          if (!delivery || cancelled) {
            return;
          }

          const deliveryArticles = delivery.articles ?? [];
          const deliveryAdditionalItems = delivery.additional_items ?? [];
          const loggedUser = userData
            ? {
                id: userData.id,
                label: `${userData.first_name} ${userData.last_name}`,
              }
            : null;
          setMovement({
            ...delivery,
            created_by: loggedUser?.id ?? delivery.created_by,
            movement_type_id: MovementTypes.Discharge,
            date: getInitialMovementDate(),
            articles: deliveryArticles.map((a, index) => ({
              ...a,
              id: index,
              article_locations: {
                id: a.article_locations.id,
                stock: a.article_locations.stock,
                reserved: a.article_locations.reserved,
                not_available: a.article_locations.not_available,
                in_work_orders: a.article_locations.in_work_orders,
                article_id: a.article_locations.article_id,
                location_id: a.article_locations.location_id,
                article: a.article_locations.article,
                location: a.article_locations.location,
              },
              article_locations_id: a.article_locations.id,
              article_id: a.article_locations.article.id,
              amount_per_container: a.amount_per_container,
              client_id: a.article_locations.article.client.id,
              location_id: a.article_locations.location_id,
              container: a.article_locations.article.container.description,
              description: a.article_locations.article.description,
              unit: a.article_locations.article.unit.description,
              presentation: a.presentation ?? null,
              is_reserved: true,
              reserved_reference_amount: a.amount,
            })),
            additional_items: deliveryAdditionalItems.map((ai, index) => ({
              ...ai,
              id: index,
              description:
                additionalItems.find((a) => a.id === ai.additional_item_id)
                  ?.description || "",
            })),
          });
          setSelectedUser(loggedUser);
          setSelectedAuthor({
            id: delivery.updated_by,
            label: `${
              users.find((u) => u.id === delivery.updated_by)?.first_name
            } ${users.find((u) => u.id === delivery.updated_by)?.last_name}`,
          });
          setSelectedClient({
            id: delivery.client_id,
            label: `${
              clients.find((c) => c.id === delivery.client_id)?.razon_social
            }`,
          });
          setSelectedAssociatedClient({
            id:
              clientAssociatedClients.find(
                (ac) => ac.razon_social === delivery.associated_client,
              )?.id ?? 0,
            label: delivery.associated_client,
          });
          setArticleRowIndex(deliveryArticles.length);
        } catch (error) {
          console.error(error);
          showSnackBar("No se pudo cargar el pedido seleccionado.", "error");
        } finally {
          if (!cancelled) {
            setMovementLoading(false);
          }
        }
      };

      setSelectedDeliveryMovement();
    }

    return () => {
      cancelled = true;
    };
  }, [
    selectedDelivery,
    clientId,
    clients,
    userData,
    storeId,
    storesList,
    movementToInitialize,
  ]);

  useEffect(() => {
    const getMovementDraft = async (draftId: number) => {
      setMovementLoading(true);
      const draftMovementResponse = await getMovements(
        draftId.toString(),
        true,
      );
      if (
        draftMovementResponse.status === 200 &&
        draftMovementResponse.data.length > 0 &&
        draftMovementResponse.data[0] !== null
      ) {
        const draftMovement = draftMovementResponse.data[0];
        setNewPrepOrder(
          draftMovement.movement_type_id === MovementTypes.PreparationDraft,
        );
        setMovementToInitialize({
          ...draftMovement,
          date: dateToMySQLFormat(draftMovement.date),
          articles: hydrateDraftMovementArticles(draftMovement.articles || []),
        });
      }
      setMovementLoading(false);
    };
    if (movement.movement_draft_id) {
      getMovementDraft(movement.movement_draft_id);
    }
  }, [movement.movement_draft_id]);

  useEffect(() => {
    if (movementToPrint) {
      handlePrint();
    }
  }, [movementToPrint]);

  useEffect(() => {
    if (!isPalletCalculationEnabled) {
      return;
    }

    if (mustCalculatePallets) calculateTotalPallets();
    else setMustCalculatePallets(true);
  }, [
    movement.articles,
    calculateTotalPallets,
    isPalletCalculationEnabled,
    mustCalculatePallets,
  ]);

  const isDischargeWithDelivery = useMemo(() => {
    return !!selectedDelivery?.id && !newPrepOrder;
  }, [selectedDelivery, newPrepOrder]);

  useEffect(() => {
    if (onMovementChange) {
      onMovementChange(movement);
    }
  }, [onMovementChange, movement]);

  useEffect(() => {
    if (onMovementChange) {
      onMovementChange(movement);
    }
  }, [onMovementChange, movement]);

  useEffect(() => {
    if (newPrepOrder) {
      setMovement((prev) => ({
        ...prev,
        isAdjustment: false,
        movement_id: null,
      }));
    }
  }, [newPrepOrder]);

  useEffect(() => {
    if (movement.isAdjustment) {
      setNewPrepOrder(false);
    }
  }, [movement.isAdjustment]);

  useEffect(() => {
    const loadMovements = async () => {
      if (
        !newPrepOrder &&
        movement.isAdjustment &&
        movement.client_id &&
        movement.associated_client
      ) {
        const query = `?client_id=${movement.client_id}&movement_type_id=${movement.movement_type_id}&associatedClient=${encodeURIComponent(movement.associated_client)}`;
        const res = await fetchMovements(query, true);
        const movements = res.data?.result ?? [];
        setAvailableMovements(Array.isArray(movements) ? movements : []);
      } else {
        setAvailableMovements([]);
      }
    };

    loadMovements();
  }, [
    newPrepOrder,
    movement.isAdjustment,
    movement.client_id,
    movement.associated_client,
    movement.movement_type_id,
  ]);

  useEffect(() => {
    if (!isForLiquidation) {
      initMovementDate(movement.date, setMovementDate);
    }
  }, [isForLiquidation]);

  useEffect(() => {
    const loadNextAdjustmentDocument = async () => {
      if (!movement.isAdjustment) return;
      if (!movement.movement_id) return;

      const response = await fetchNextAdjustmentDoc(
        `${movement.movement_id}`,
        true,
      );

      if (response.status !== STATUS_CODES.OK) return;

      setMovement((prev) => ({
        ...prev,
        client_document: response.data.client_document,
      }));
    };

    loadNextAdjustmentDocument();
  }, [movement.isAdjustment, movement.movement_id]);

  useEffect(() => {
    if (movement_draft_id) return;
    if (newPrepOrder) return;

    getMovements(
      undefined,
      true,
      { movementTypeId: MovementTypes.Preparation },
      true,
    );
  }, [newPrepOrder, movement_draft_id]);

  return (
    <PageLayout
      pageTitle={
        isForLiquidation
          ? "¿Desea agregar un egreso a futuro a la liquidación?"
          : "Egresos"
      }
      pageDesc="Orden de egreso de mercaderí­a"
      importButton={
        isForLiquidation ? undefined : (
          <FileImport
            movementType={
              newPrepOrder
                ? MOVEMENT_TYPES.PREPARATION.label
                : MOVEMENT_TYPES.DISCHARGE.label
            }
            locations={locations}
            clients={clients}
            associatedClients={associatedClients}
            articles={articles}
            additional_items={additionalItems}
            users={users}
            stores={stores}
          />
        )
      }
    >
      {movementToPrint && (
        <PrintLayout
          ref={printRef}
          headerInfo={
            <OperationPrintHeaderDetails
              movement={movementToPrint}
              stores={stores}
            />
          }
          footerInfo={<OperationPrintTotals movement={movement} />}
        >
          <OperationPrintDetails
            users={users}
            movement={movementToPrint}
            client={movementToPrint.client || null}
          />
        </PrintLayout>
      )}
      <form
        onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
          e.preventDefault();
          showModal({
            title: "Confirmar",
            message: newPrepOrder
              ? "¿Está seguro que desea confirmar el pedido a preparar?"
              : "¿Está seguro que desea confirmar el egreso?",
            action: async () => {
              if (movement.isAdjustment) {
                await handleSubmit(true);
                return;
              }

              setAdditionalItemsInitialItems([]);
              setAdditionalItemsModalOpen(true);
            },
          });
        }}
      >
        {movementLoading ? (
          <Loader
            isLoading={movementLoading}
            isFirstLoad={onMovementFirstLoad}
          />
        ) : (
          <>
            <Grid container columnSpacing={1}>
              {!newPrepOrder && !isForLiquidation && (
                <Grid item xs={12} sx={{ display: "flex", gap: 1 }}>
                  <Typography variant="body2" fontWeight="bold">
                    Orden nº
                  </Typography>
                  <Typography variant="body2" fontWeight="bold" color="primary">
                    {nroOrden}
                  </Typography>
                </Grid>
              )}
              
              {!newPrepOrder && (
                <>
                  {userData?.role === Roles.SUPERADMIN && (
                    <Grid
                      item
                      xs={4}
                      sx={{ display: "flex", gap: 1, alignItems: "center" }}
                    >
                      <Typography variant="body2" fontWeight="bold">
                        Movimiento
                      </Typography>

                      <Switch
                        data-cy="adjustment-switch"
                        checked={movement.isAdjustment ?? false}
                        onChange={(e) => {
                          handleChange("isAdjustment", e.target.checked);
                          if (!e.target.checked)
                            handleChange("movement_id", null);
                        }}
                        disabled={!!movement.movement_draft_id}
                      />

                      <Typography variant="body2" fontWeight="bold">
                        Ajuste de inventario
                      </Typography>
                    </Grid>
                  )}

                  {userData?.role === Roles.SUPERADMIN && movement.isAdjustment && (
                    <Grid item xs={4}>
                      <FormControl
                        fullWidth
                        margin="none"
                        sx={{ maxWidth: 400 }}
                      >
                        <Autocomplete
                          data-cy="adjustment-movement"
                          id="adjustment-movement"
                          autoComplete
                          options={availableMovements.map((m) => ({
                            id: m.id,
                            date: m.date,
                            label: `#${m.id} - ${new Date(m.date).toLocaleDateString()} - ${m.client_document || "s/doc. del cliente"}`,
                          }))}
                          value={
                            movement.movement_id
                              ? {
                                  id: movement.movement_id,
                                  date: availableMovements.find((m) => m.id === movement.movement_id)?.date ?? movement.date,
                                  label: `Movimiento #${movement.movement_id}`,
                                }
                              : null
                          }
                          isOptionEqualToValue={(option, value) =>
                            option.id === value.id
                          }
                          onChange={(e, value) => {
                            handleChange("movement_id", value?.id ?? null);
                            if (value?.date) {
                              handleChange("date", dateISOFormat(value.date));
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Movimiento principal"
                            />
                          )}
                        />
                      </FormControl>
                    </Grid>
                  )}

                  {movement.isAdjustment &&
                    (!movement.client_id || !movement.associated_client) && (
                      <Grid item xs={4}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 0.5, display: "block" }}
                        >
                          ⚠️ Seleccione cliente y cliente asociado para ver los
                          movimientos disponibles.
                        </Typography>
                      </Grid>
                    )}
                </>
              )}

              {!newPrepOrder && !isForLiquidation && (
                <Grid item xs={4} display="flex" alignItems="center" gap={2}>
                  <FormControl sx={{ width: "100%" }} margin="none">
                    <Autocomplete
                      data-cy="prep-order"
                      id="prepOrder"
                      autoComplete
                      disableClearable
                      options={prepList}
                      value={selectedDelivery}
                      disabled={!!movement_draft_id}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      onChange={(
                        e,
                        value: {
                          id: number | undefined;
                          label: string;
                        } | null,
                      ) => setSelectedDelivery(value || { id: 0, label: "" })}
                      renderInput={(params) => (
                        <TextField {...params} label="Pedido a preparar" />
                      )}
                      renderOption={(props, option) => (
                        <li {...props} data-cy={option.label}>
                          {option.label}
                        </li>
                      )}
                    />
                  </FormControl>
                </Grid>
              )}

              <Grid item xs={4}>
                <FormControl fullWidth margin="none">
                  <Autocomplete
                    id="user"
                    autoComplete
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    value={selectedUser}
                    disabled
                    options={users.map((u) => ({
                      id: u.id,
                      label: `${u.first_name} ${u.last_name}`,
                    }))}
                    onChange={(
                      e,
                      value: { id: number; label: string } | null,
                    ) => {
                      handleChange("created_by", value?.id);
                      setSelectedUser(value || null);
                    }}
                    fullWidth
                    renderInput={(params) => (
                      <TextField {...params} required label="Usuario" />
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth margin="none">
                  <Autocomplete
                    data-cy="author"
                    id="author"
                    autoComplete
                    value={selectedAuthor}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    disabled={isDischargeWithDelivery || !movement.created_by}
                    options={users.map((u) => ({
                      id: u.id,
                      label: `${u.first_name} ${u.last_name}`,
                    }))}
                    onChange={(
                      e,
                      value: { id: number; label: string } | null,
                    ) => {
                      handleChange("updated_by", value?.id);
                      setSelectedAuthor(value || null);
                    }}
                    renderInput={(params) => (
                      <TextField {...params} required label="Preparó" />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} data-cy={option.label}>
                        {option.label}
                      </li>
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth margin="none">
                  <DateTimePicker
                    disableFuture={!isForLiquidation}
                    minDate={minDate}
                    maxDate={maxDate}
                    label="Fecha *"
                    viewRenderers={{
                      hours: renderTimeViewClock,
                      minutes: renderTimeViewClock,
                      seconds: renderTimeViewClock,
                    }}
                    slots={{
                      actionBar: DateTimePickerActionBar,
                    }}
                    slotProps={{
                      textField: {
                        inputProps: {
                          "data-cy": `date`,
                        },
                      },
                    }}
                    views={DATE_VIEWS}
                    value={movement.date ? new Date(movement.date) : null}
                    disabled={
                      isDischargeWithDelivery ||
                      (movement.isAdjustment && !!movement.movement_id)
                    }
                    onChange={(value: Date | null) => {
                      try {
                        if (!value) {
                          handleChange("date", null);
                          return;
                        }
                        if (value?.toISOString()) {
                          handleChange(
                            "date",
                            dateISOFormat(value.toISOString()),
                          );
                        }
                      } catch (dateExcep) {
                        return;
                      }
                    }}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={4}>
                <FormControl fullWidth margin="none">
                  <Autocomplete
                    data-cy="client"
                    id="client"
                    autoComplete
                    disabled={isDischargeWithDelivery || isForLiquidation}
                    value={selectedClient}
                    options={clients.filter((c) => c.parent_client_id == null).map((c) => ({
                      id: c.id,
                      label: `${c.code} - ${c.razon_social}`,
                    }))}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    onChange={(
                      e,
                      value: {
                        id: number;
                        label: string;
                      } | null,
                    ) => {
                      setSelectedClient(value || null);
                      setSelectedAssociatedClient(null);
                      setMovement((prev) => ({
                        ...prev,
                        client_id: value?.id ?? 0,
                        articles: [],
                        associated_client: "",
                      }));
                    }}
                    renderInput={(params) => (
                      <TextField {...params} required label="Cliente" />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} data-cy={option.label}>
                        {option.label}
                      </li>
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth margin="none">
                  <Autocomplete
                    data-cy="associated-client"
                    id="associated-client"
                    value={selectedAssociatedClient}
                    options={clientAssociatedClients?.map((c) => ({
                      id: c.id,
                      label: c.razon_social,
                    }))}
                    isOptionEqualToValue={(option, value) =>
                      option.label === value.label
                    }
                    disabled={!movement.client_id}
                    onChange={(
                      e,
                      value: { id: number; label: string } | null,
                    ) => {
                      handleChange("associated_client", value?.label);
                      setSelectedAssociatedClient(value || null);
                    }}
                    fullWidth
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        label="Cliente Asociado"
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} data-cy={option.label}>
                        {option.label}
                      </li>
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth required margin="none">
                  <TextField
                    data-cy="income-client-doc"
                    label="Documento del Cliente"
                    value={movement.client_document}
                    onChange={(e) =>
                      handleChange("client_document", e.target.value)
                    }
                    disabled={isDischargeWithDelivery}
                    fullWidth
                    required
                  />
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth margin="none">
                  <Autocomplete
                    data-cy="warehouse"
                    id="warehouse"
                    autoComplete
                    value={selectedWarehouse}
                    disabled={isDischargeWithDelivery || isForLiquidation}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    options={storesList}
                    onChange={(
                      e,
                      value: { id: number | null; label: string } | null,
                    ) => {
                      setMovement((prev) => ({
                        ...prev,
                        store_id: value?.id === 0 ? null : (value?.id ?? null),
                        articles: [],
                      }));
                    }}
                    fullWidth
                    renderInput={(params) => (
                      <TextField {...params} required label="Almacen" />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} data-cy={option.label}>
                        {option.label}
                      </li>
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth margin="none">
                  <CustomNumberInput
                    label="Pallets"
                    value={movement.pallets}
                    onChange={(e) => {
                      setIsPalletCalculationEnabled(false);
                      handleChange("pallets", Number(e.target.value));
                    }}
                    required
                  />
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth margin="none">
                  <CustomNumberInput
                    label="Peso"
                    value={movement.weight}
                    onChange={(e) => {
                      setIsWeightCalculationEnabled(false);
                      handleChange("weight", Number(e.target.value));
                    }}
                    required
                  />
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth margin="none">
                  <CustomNumberInput
                    data-cy="remito"
                    label="Número de Remito"
                    value={movement.numero_remito}
                    onChange={(e) =>
                      handleChange("numero_remito", e.target.value)
                    }
                  />
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth margin="none">
                  <TextField
                    data-cy="obs"
                    id="observations"
                    label="Observaciones"
                    type="text"
                    fullWidth
                    value={movement.observation}
                    onChange={(e) =>
                      handleChange("observation", e.target.value)
                    }
                  />
                </FormControl>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" fontWeight="bold" sx={{ mt: 2 }}>
                Artículos
              </Typography>

              <Grid item xs={12} my={2}>
                <ArticlesList
                  movement={movement}
                  onMovementUpdate={handleChange}
                  operationType={MOVEMENT_TYPES.DISCHARGE.label}
                  locations={locations}
                  stores={stores}
                  hasPalletAutocalculation={isPalletCalculationEnabled}
                  hasWeightAutocalculation={isWeightCalculationEnabled}
                  rowIndex={articleRowIndex}
                  setRowIndex={setArticleRowIndex}
                  client={selectedFullClient}
                />

                <Grid
                  container
                  spacing={1}
                  sx={{ mt: 1 }}
                  justifyContent="flex-start"
                >
                  <Grid item xs={12} md={3}>
                    <TextField
                      label="Cantidad Total"
                      value={String(movementTotals.totalCantidad)}
                      fullWidth
                      disabled
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <TextField
                      label="Total Unidad"
                      value={String(movementTotals.totalSubtotalUnidad)}
                      fullWidth
                      disabled
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            {!isForLiquidation && (
              <Grid item xs={12}>
                {(selectedDelivery.id === 0 || newPrepOrder) &&
                  !movement.isAdjustment && (
                    <Button
                      data-cy="draft-income-button"
                      variant="contained"
                      onClick={() => handleSendDraft()}
                      sx={{ marginRight: 2 }}
                    >
                      Guardar Borrador
                    </Button>
                  )}
                <Button
                  data-cy="confirm-prep-discharge-button"
                  type="submit"
                  variant="contained"
                >
                  {newPrepOrder ? "Guardar Pedido" : "Guardar Egreso"}
                </Button>
              </Grid>
            )}
          </>
        )}
      </form>
      <MovementAdditionalItemsStepModal
        open={additionalItemsModalOpen}
        title={getAdditionalItemsModalTitle({
          ...movement,
          movement_type_id: newPrepOrder
            ? MovementTypes.Preparation
            : MovementTypes.Discharge,
        })}
        movement={movement}
        additionalItems={clientAdditionalItems}
        childClients={childClients}
        associatedClients={associatedClients}
        initialItems={additionalItemsInitialItems}
        onClose={() => {
          setAdditionalItemsModalOpen(false);
          setAdditionalItemsInitialItems([]);
        }}
        onConfirm={handleConfirmAdditionalItems}
      />
    </PageLayout>
  );
}
